import { localizableSnapshot, withCartCode } from '@/features/cart/cartCode';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useGetOrderQuery } from '@/features/checkout/ordersApi';
import { useAppSelector } from '@/app/hooks';
import { resourceApi } from '@/app/api/resourceApi';
import { selectIsSharedTableCart } from '@/features/tableSession/tableSessionSlice';
import { selectIsAuthenticated } from '@/features/user/selectors';
import { useGetLoyaltyBalanceQuery, useGetLoyaltyCardQuery } from '@/features/loyalty/loyaltyApi';
import { earnEstimate, firstNameOf } from '@/features/loyalty/format';
import GuestConfirmCta from '@/features/loyalty/components/GuestConfirmCta';
import Price, { formatPrice } from '@/components/ui/Price';
import NotFoundPage from '@/pages/NotFoundPage';
import TrackingLinkExpired from './TrackingLinkExpired';
import { statusLabel, paymentLabel } from '@/features/checkout/orderLabels';
import { orderDisplayNumber } from '@/features/checkout/orderNumber';
import CancellationPolicyNotice from '@/features/restaurant/CancellationPolicyNotice';
import { msUntilNextPollStep, trackingPollIntervalMs } from './trackingPolling';
import { scheduledDayLabel } from '@/features/checkout/dayLabel';
import { useGetRestaurantIdentityQuery } from '@/app/api/hubApi';
import { formatTimeInTz, restaurantTz } from '@/lib/hours';


export default function ConfirmationPage() {
  const { t, i18n } = useTranslation();
  const { restaurantUuid = '', tableToken, orderId = '' } = useParams<{
    restaurantUuid: string;
    tableToken?: string;
    orderId: string;
  }>();
  const [searchParams] = useSearchParams();
  const backTarget = tableToken ? `/${restaurantUuid}/table/${tableToken}` : `/${restaurantUuid}`;
  const backLabel = tableToken ? t('checkout.dineIn.retourTable') : t('confirmation.backToMenu');
  // SC-169: when the guest is still in a shared dine-in session, nudge them that
  // adding more reuses the same table flow (a new round, not a new bill).
  const isShared = useAppSelector(selectIsSharedTableCart);
  // Deja en cache : `CancellationPolicyNotice` plus bas interroge la meme fiche.
  // On n'en lit que le pays, pour dater le creneau dans le fuseau du restaurant.
  const { data: identity } = useGetRestaurantIdentityQuery(restaurantUuid, {
    skip: !restaurantUuid,
  });
  // SC-1278 — menu déjà en cache (jamais demandé ici) pour afficher les plats
  // dans la langue choisie ; absent ⇒ noms du serveur.
  const menu = useAppSelector(resourceApi.endpoints.getMenu.select(restaurantUuid)).data;
  const [pollingStopped, setPollingStopped] = useState(false);
  // Cadence dégressive : 5 s, puis 30 s, puis 2 min, puis plus rien (voir
  // trackingPolling.ts). L'arrêt sur statut terminal ci-dessous ne suffisait
  // pas — une commande que le restaurant n'avance jamais reste `received`, et
  // la page l'interrogeait indéfiniment : 2 744 requêtes en 15 h mesurées en
  // prod le 2026-09-01 pour une seule commande déjà remise.
  const [elapsedMs, setElapsedMs] = useState(0);
  const { data: order, isLoading, isError, error } = useGetOrderQuery(orderId, {
    skip: !orderId,
    pollingInterval: pollingStopped ? 0 : trackingPollIntervalMs(elapsedMs),
  });
  // L5 earn feedback: points are credited async (webhook on PAID), so this shows
  // the CURRENT balance rather than a computed "+N" for this order. Guests
  // (no consumer token) never hit the IsAuthenticated loyalty endpoint.
  const isAuthenticated = useAppSelector(selectIsAuthenticated);
  const { data: loyalty } = useGetLoyaltyBalanceQuery(restaurantUuid, {
    skip: !isAuthenticated || !restaurantUuid,
  });
  // SC-1091 M11 — the earn RULES (per euro / per visit), to say roughly what this
  // order brings. Same gates as the balance: logged in and a running program.
  const { data: loyaltyCard } = useGetLoyaltyCardQuery(restaurantUuid, {
    skip: !isAuthenticated || !restaurantUuid || !loyalty?.enabled,
  });

  useEffect(() => {
    setPollingStopped(false);
    setElapsedMs(0);
  }, [orderId]);

  // Un seul minuteur à la fois : il ne sert qu'à franchir le palier suivant,
  // pas à compter le temps. Le dernier palier ne programme rien — c'est l'arrêt.
  useEffect(() => {
    if (pollingStopped) return;
    const delay = msUntilNextPollStep(elapsedMs);
    if (delay === null) return;
    const timer = setTimeout(() => setElapsedMs(elapsedMs + delay), delay);
    return () => clearTimeout(timer);
  }, [elapsedMs, pollingStopped]);

  useEffect(() => {
    if (order && ['ready', 'handed_over', 'cancelled'].includes(order.status)) {
      setPollingStopped(true);
    }
  }, [order?.status]);

  useEffect(() => {
    if (isError) {
      setPollingStopped(true);
    }
  }, [isError]);

  if (isLoading) return null;
  // Un jeton de suivi périmé et une commande inexistante (404) ne se disent pas
  // de la même façon : le premier a une sortie, le second non.
  //
  // DF-0179 — 410 est le code que `sale_api` rend désormais quand la commande
  // EXISTE et que seule l'autorisation a échoué. Sans lui cet écran était
  // inatteignable : le serveur répondait 404 pour un jeton périmé comme pour
  // une commande inconnue, et le client qui rouvrait son propre lien 4 h plus
  // tard retombait sur la page morte (mesuré en prod le 2026-09-07 : 0/3).
  // 401/403 restent acceptés — d'autres chemins d'autorisation les rendent.
  const errorStatus = error && 'status' in error ? error.status : undefined;
  if (errorStatus === 401 || errorStatus === 403 || errorStatus === 410) {
    return (
      <TrackingLinkExpired
        restaurantUuid={restaurantUuid}
        backTarget={backTarget}
        backLabel={backLabel}
      />
    );
  }
  if (isError || !order) return <NotFoundPage />;

  // SC-346: a QR-table guest's rounds all merge into one anchor order, so `order`
  // carries every round's lines + a cumulative total. The submit flow pins the
  // just-sent round in `?round=`; scope the recap to it so the guest sees only the
  // dishes that were just sent to the kitchen — not the previous rounds. No param
  // (online take-out/delivery, legacy links) → show the whole order unchanged. If
  // the pinned round matches nothing, fall back to the full order rather than a
  // blank recap.
  // DF-0586 — `?round=` may list several rounds (« 1,2,3 »): a send « service
  // par service » creates its first service now and holds the next ones.
  const pinnedRounds = (searchParams.get('round') ?? '')
    .split(',')
    .filter((part) => part.trim() !== '')
    .map(Number)
    .filter((n) => Number.isFinite(n));
  const roundLines = pinnedRounds.length
    ? order.lines.filter((line) => line.roundNumber != null && pinnedRounds.includes(line.roundNumber))
    : [];
  // DF-0586 — the services the table chose to hold back, named so the guest
  // knows they will come later, when the waiter sends them.
  const heldServices = (order.heldRounds ?? [])
    .map((round) => order.roundServiceLabels?.[String(round)] ?? '')
    .filter(Boolean);
  const isRoundView = roundLines.length > 0;
  const displayLines = isRoundView ? roundLines : order.lines;
  const roundSubtotal = roundLines.reduce((sum, line) => sum + line.unitPrice * line.qty, 0);
  // Dine-in has no delivery fee or order-level discount, so the round total is just
  // its line subtotal.
  const displayTotals = isRoundView
    ? { subtotal: roundSubtotal, deliveryFee: 0, total: roundSubtotal, discountAmountCents: 0, discountPercent: 0 }
    : order.totals;
  const earnedEstimate = earnEstimate(displayTotals.total, loyaltyCard?.points_per_euro, loyaltyCard?.points_per_visit);
  // SC-1081 — l'heure de retrait se lit a l'horloge du restaurant.
  const formatTime = (iso: string) => formatTimeInTz(iso, restaurantTz(identity ?? {}));
  const estimateDayLabel = scheduledDayLabel(
    order.estimatedReadyAt,
    new Date(),
    i18n.language,
    identity
  );

  return (
    <div className="max-w-[680px] mx-auto px-4 py-12 space-y-6">
      <div className="text-center">
        <div className="inline-block w-24 h-24 bg-mint border-2 border-ink shadow-stamp flex items-center justify-center mb-4 animate-[stampDrop_400ms_cubic-bezier(0.34,1.56,0.64,1)]">
          <span className="text-4xl">✓</span>
        </div>
        <h1 className="font-display text-3xl text-ink">{t('confirmation.title')}</h1>
        <p className="font-mono text-sm mt-2">{t('confirmation.orderNumber', { number: orderDisplayNumber(order) })}</p>
        {/* Une pre-commande pour un autre jour nomme ce jour : l'heure seule se
            lit « dans un instant » sur l'ecran qui suit le paiement, et
            « demain » est ambigu passe minuit (DF-0273). Le jour est celui du
            restaurant, comme l'onglet retenu au tunnel et le bon imprime. */}
        <p className="font-mono text-sm">
          {/* SC-1500 — « Dès que possible » d'abord, l'heure estimée en second. */}
          {order.isAsap && (
            <span data-testid="confirmation-asap">{t('checkout.slotAsap')} · </span>
          )}
          {estimateDayLabel
            ? t('confirmation.estimateDay', {
                day: estimateDayLabel,
                time: formatTime(order.estimatedReadyAt)
              })
            : t('confirmation.estimate', { time: formatTime(order.estimatedReadyAt) })}
        </p>
        <p className="font-mono text-sm mt-2">
          {t('confirmation.statusLine', {
            status: statusLabel(order.status, t),
            payment: paymentLabel(order.paymentStatus, t),
          })}
        </p>
        {/* SC-1489 — une partie de nos e-mails part dans les spams : on le dit
            ici, comme la roue OuiLOTO (SC-1482). sale_api n'envoie la
            confirmation qu'une fois la commande sortie de `pending_payment` (au
            paiement en ligne, ou des la creation sinon) ; une commande annulee
            n'en parle pas. */}
        {order.customer.email && !['pending_payment', 'cancelled'].includes(order.status) && (
          <p className="font-mono text-sm mt-2 text-ink-mute break-words" data-testid="confirmation-email-sent">
            {t('confirmation.emailSent', { email: order.customer.email })}
          </p>
        )}
      </div>

      <div className="border-2 border-ink bg-paper p-5 shadow-stamp">
        <h2 className="font-display text-xl mb-3">{t('confirmation.summary')}</h2>
        {heldServices.length > 0 && (
          <p className="font-mono text-sm mb-3" data-testid="confirmation-held-services">
            {t('serviceChoice.heldNotice', { services: heldServices.join(', ') })}
          </p>
        )}
        <ul className="font-mono text-sm space-y-2">
          {displayLines.map((line) => (
            <li key={line.lineId}>
              {/* DF-0260 : gap-3 + min-w-0. Sans eux, des que « quantite + code +
                  nom » remplit la ligne, justify-between n'a plus d'espace a
                  distribuer et le prix se colle au dernier mot
                  (« Cheesecake Basque & glace8,90 € », iPhone 390 px).
                  break-words : min-w-0 autorise la colonne a devenir plus etroite
                  que son plus long mot, qui deborderait alors sur le prix. */}
              <div className="flex justify-between gap-3">
                <span className="min-w-0 break-words">{line.qty}× {withCartCode(localizableSnapshot(line, menu))}</span>
                <Price amountCents={line.unitPrice * line.qty} className="shrink-0" />
              </div>
              {line.optionNames && line.optionNames.length > 0 && (
                <ul className="list-none pl-4 m-0 text-xs text-ink/70 leading-tight">
                  {line.optionNames.map((name, i) => (
                    <li key={i}>· {name}</li>
                  ))}
                </ul>
              )}
              {line.note && (
                <p className="m-0 pl-4 text-xs italic text-ink/60">“{line.note}”</p>
              )}
            </li>
          ))}
        </ul>
        <hr className="border-ink/30 my-3" />
        <div className="flex justify-between font-mono text-sm">
          <span>{t('order.subtotal')}</span><Price amountCents={displayTotals.subtotal} />
        </div>
        {displayTotals.discountAmountCents && displayTotals.discountAmountCents > 0 ? (
          <div className="flex justify-between font-mono text-sm">
            <span>{t('order.discountLabel')} −{displayTotals.discountPercent ?? 0}%</span>
            <Price amountCents={-displayTotals.discountAmountCents} />
          </div>
        ) : null}
        {displayTotals.loyaltyDiscountCents && displayTotals.loyaltyDiscountCents > 0 ? (
          <div className="flex justify-between font-mono text-sm" data-testid="loyalty-discount-line">
            <span>{t('loyalty.checkout.discountLine')}</span>
            {/* Même signe « − » que l'encart du paiement où l'avantage a été choisi. */}
            <span className="font-mono font-bold text-ink whitespace-nowrap">
              <bdi dir="ltr">−{formatPrice(displayTotals.loyaltyDiscountCents)}</bdi>
            </span>
          </div>
        ) : null}
        {displayTotals.deliveryFee > 0 && (
          <div className="flex justify-between font-mono text-sm">
            <span>{t('order.deliveryLine')}</span><Price amountCents={displayTotals.deliveryFee} />
          </div>
        )}
        <div className="flex justify-between font-display text-lg mt-2">
          <span>{t('order.total')}</span><Price amountCents={displayTotals.total} />
        </div>
      </div>

      {loyalty?.enabled && (
        <div
          className="bg-paper border-2 border-ink shadow-stamp p-4 rounded-btn flex items-center justify-between"
          data-testid="loyalty-confirmation-card"
        >
          <span className="font-display text-ink">{t('loyalty.confirmationTitle')}</span>
          <span className="font-display text-lg text-[var(--on-primary)] bg-mint px-3 py-1 rounded-btn-sm">
            {t('loyalty.pointsUnit', { n: loyalty.balance })}
          </span>
        </div>
      )}
      {/* Only an order paid ONLINE earns points (credited by the payment webhook);
          on site, the customer shows the card at the counter instead. */}
      {loyalty?.enabled && !tableToken && order.payment.channel === 'online' && earnedEstimate > 0 && (
        <p className="m-0 font-mono text-sm text-ink-mute" data-testid="loyalty-confirmation-earn">
          <span aria-hidden="true">🎁 </span>
          {t('loyalty.confirmationEarn', { n: earnedEstimate })}
        </p>
      )}
      {loyalty?.enabled && !tableToken && order.payment.channel === 'on_site' && (
        <p className="m-0 font-mono text-sm text-ink-mute" data-testid="loyalty-confirmation-counter">
          <span aria-hidden="true">🎁 </span>
          {t('loyalty.checkout.earnCounter')}
        </p>
      )}

      {/* SC-1091 — a GUEST order: create an account to earn points from the next one.
          Honest: orders placed without an account are not credited afterwards. */}
      {!isAuthenticated && (
        <GuestConfirmCta
          restaurantUuid={restaurantUuid}
          skip={Boolean(tableToken)}
          prefill={{
            firstName: firstNameOf(order.customer?.name),
            lastName: (order.customer?.name ?? '').trim().split(/\s+/).slice(1).join(' '),
            email: order.customer?.email || undefined,
            phone: order.customer?.phone || undefined,
          }}
        />
      )}

      {tableToken && isShared && (
        <div
          className="border-2 border-ink bg-mint/20 p-3 font-mono text-sm text-ink/80 shadow-stamp"
          data-testid="shared-add-more-hint"
        >
          {t('tableShared.addMoreHint')}
        </div>
      )}

      {/* SC-211 (mirrors SC-197 §5): the no-cancellation / call-the-restaurant
          notice is for takeaway & delivery; a seated QR-table guest is already
          on-site and asks the staff directly, so it has no value here. */}
      {!tableToken && <CancellationPolicyNotice restaurantUuid={restaurantUuid} />}

      <div className="flex flex-col sm:flex-row gap-3 justify-center">
        <div className="bg-paper border-2 border-ink rounded-btn px-6 py-3 font-display text-center">
          {statusLabel(order.status, t)}
        </div>
        <Link
          to={backTarget}
          className="bg-mint border-2 border-ink rounded-btn px-6 py-3 font-display text-center shadow-stamp-sm"
        >
          {backLabel}
        </Link>
      </div>

      <style>{`
        @keyframes stampDrop {
          from { transform: translateY(-30px) rotate(-8deg) scale(1.2); opacity: 0; }
          to   { transform: translateY(0) rotate(0) scale(1); opacity: 1; }
        }
      `}</style>
    </div>
  );
}
